import moment from 'moment';
import lodashInvert from 'lodash/fp/invert';
import lodashPick from 'lodash/fp/pick';
import lodashRound from 'lodash/round';
import lodashOmit from 'lodash/fp/omit';
import { stringify } from 'querystring';

import typeCheck from 'common/util/typeCheck';
import parseBoolean from 'common/util/parseBoolean';

import { USER_ROLE_SUNROOM_CLUB } from 'common/userRoles';

import { INCOME_VERIFICATION_STATUS_COMPLETED } from 'common/incomeVerificationConstants';

import {
  VERIFICATION_OF_RENTAL_STATUS_OWN,
  VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY,
} from 'common/verificationOfRentalConstants';

import getApplicationFormFields, {
  editableFieldsFieldgroup,
} from 'common/householdApplication/applicationFormFields';

import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_PROCESSING,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_INTERNAL_REVIEW,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_APPLICATION_STATUS_LIST,
  HOUSEHOLD_APPLICATION_STATUS_GROUP_DEFAULT,
  HOUSEHOLD_APPLICATION_STATUS_GROUP_SR,
  HOUSEHOLD_APPLICATION_STATUS_GROUP_GUARANTOR,
  HOUSEHOLD_APPLICATION_STATUS_OPTION_ITEMS_TO_GROUP_MAPPING,
  HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_RECENTLY_EMPLOYED,
  HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING,
} from 'common/householdConstants';

import {
  FRONT_TAG_WANTS_TO_APPLY,
  FRONT_TAG_STARTED_APPLICATION,
  FRONT_TAG_COMPLETED_APPLICATION,
  FRONT_TAG_PAID_APPLICATION_FEE,
  FRONT_TAG_PENDING_DOCUMENTS,
  FRONT_TAG_PENDING_TRANSUNION,
  FRONT_TAG_PENDING_VERIFICATION,
  FRONT_TAG_APPLICATION_DELIVERED,
  FRONT_TAG_PENDING_LANDLORD,
  FRONT_TAG_APPLICATION_ACCEPTED,
  FRONT_TAG_DEPOSIT_PAID,
  FRONT_TAG_LEASE_DRAFT_PENDING,
  FRONT_TAG_LEASE_SENT,
  FRONT_TAG_LEASE_SIGNED,
} from 'common/frontConstants';

import { LISTING_DATA_SOURCE_SUNROOM } from 'common/listingConstants';

import { SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED } from 'common/applicationAssemblyLineConstants';

import { createFormValidationFn } from 'client/helpers/formikHelpers';
import { isListingTenantScreening } from 'common/listingHelpers';

import {
  STRIPE_CHARGE_CATEGORY_APPLICATION_FEE,
  STRIPE_CHARGE_CATEGORY_GUARANTOR_APPLICATION_FEE,
} from 'common/stripeConstants';

export const FRONT_TAG_TO_APPLICATION_STATUS_MAPPING = {
  [FRONT_TAG_WANTS_TO_APPLY]: HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  [FRONT_TAG_STARTED_APPLICATION]: HOUSEHOLD_APPLICATION_STATUS_STARTED,
  [FRONT_TAG_COMPLETED_APPLICATION]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [FRONT_TAG_PAID_APPLICATION_FEE]: HOUSEHOLD_APPLICATION_STATUS_PAID,
  [FRONT_TAG_PENDING_DOCUMENTS]: HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
  [FRONT_TAG_PENDING_VERIFICATION]: HOUSEHOLD_APPLICATION_STATUS_PROCESSING,
  [FRONT_TAG_PENDING_TRANSUNION]: HOUSEHOLD_APPLICATION_STATUS_PENDING_INTERNAL_REVIEW,
  [FRONT_TAG_PENDING_LANDLORD]: HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  [FRONT_TAG_APPLICATION_DELIVERED]: HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  [FRONT_TAG_APPLICATION_ACCEPTED]: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  [FRONT_TAG_DEPOSIT_PAID]: HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  [FRONT_TAG_LEASE_DRAFT_PENDING]: HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  [FRONT_TAG_LEASE_SENT]: HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  [FRONT_TAG_LEASE_SIGNED]: HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
};

export const APPLICATION_STATUS_TO_FRONT_TAG_MAPPING = lodashInvert(
  FRONT_TAG_TO_APPLICATION_STATUS_MAPPING,
);

export function getListingApplicationFormUrl(params) {
  typeCheck('params::NonEmptyObject', params);

  const {
    userId,
    listingId,
    householdId,
    shouldRequestTourPrompt,
    prequalListingId,
    formFieldId,
    formType,
    requestedShowing,
    applicationId,
    saveExitRedirect,
    fieldGroup,
  } = params;

  const shouldRequestTourPromptValue = parseBoolean(shouldRequestTourPrompt);

  typeCheck('userId::PositiveNumber', userId);
  typeCheck('listingId::Maybe PositiveNumber', listingId);
  typeCheck('householdId::Maybe PositiveNumber', householdId);
  typeCheck('shouldRequestTourPromptValue::Maybe Boolean', shouldRequestTourPromptValue);
  typeCheck('formType::NonEmptyString', formType);
  typeCheck('requestedShowing::Maybe Object', requestedShowing);

  const applicationFormQueryString =
    `?fieldId=${encodeURIComponent(formFieldId)}` +
    `&formType=${encodeURIComponent(formType)}` +
    (saveExitRedirect ? `&saveExitRedirect=${saveExitRedirect}` : '') +
    (fieldGroup ? `&fieldGroup=${fieldGroup}` : '') +
    (shouldRequestTourPromptValue
      ? `&shouldRequestTourPrompt=${shouldRequestTourPromptValue}`
      : '') +
    (prequalListingId ? `&prequalListingId=${prequalListingId}` : '') +
    (householdId ? `&householdId=${householdId}` : '') +
    (requestedShowing ? `&${stringify(requestedShowing)}` : '');

  const includeApplication = applicationId ? `/${applicationId}` : '';

  return listingId
    ? `/application/${userId}/${listingId}/form${includeApplication}${applicationFormQueryString}`
    : formFieldId
    ? `/application/renter-profile/${userId}/form${applicationFormQueryString}`
    : '/profile';
}

export function getHouseholdApplicationFlowStatus({
  householdApplication,
  householdApplicationSubmission,
  isGuarantor,
}) {
  const householdApplicationStatusGroup =
    HOUSEHOLD_APPLICATION_STATUS_CHILDREN_GROUP_TO_PARENT_MAPPING[householdApplication.status];

  if (isGuarantor) {
    if (!householdApplicationSubmission) {
      return HOUSEHOLD_APPLICATION_STATUS_REQUESTED;
    }
    if (!householdApplicationSubmission.stripeChargeId) {
      if (householdApplicationStatusGroup === HOUSEHOLD_APPLICATION_STATUS_COMPLETED) {
        return HOUSEHOLD_APPLICATION_STATUS_COMPLETED;
      }
      return HOUSEHOLD_APPLICATION_STATUS_STARTED;
    }
    if (householdApplicationSubmission.stripeChargeId) {
      return HOUSEHOLD_APPLICATION_STATUS_PAID;
    }
  }

  return householdApplicationStatusGroup;
}

// TODO(@d-oliveros): Check if sort is correct.
export function findHouseholdApplication({ householdApplications, category, listingId }) {
  if (!householdApplications) {
    return null;
  }
  typeCheck('householdApplications::Array', householdApplications);
  typeCheck('category::NonEmptyString', category);
  typeCheck('listingId::Maybe PositiveNumber', listingId);

  return (
    householdApplications
      .filter(
        (householdApplication) =>
          (!listingId || householdApplication.listingId === listingId) &&
          householdApplication.category === category,
      )
      .sort((a, b) => new Date(a.createdDate).getTime() < new Date(b.createdDate).getTime())
      .shift() || null
  );
}

export function findHouseholdApplicationSubmission({
  applicationSubmissions,
  applicationId,
  listingId,
}) {
  if (!applicationSubmissions) {
    return null;
  }

  typeCheck('applicationSubmissions::Array', applicationSubmissions);
  typeCheck('applicationId::PositiveNumber', applicationId);
  typeCheck('listingId::PositiveNumber', listingId);

  return applicationSubmissions.find(
    (householdSubmission) =>
      householdSubmission.applicationId === applicationId &&
      householdSubmission.listingId === listingId,
  );
}

/**
 * Gets the pure data application form fields, with control fields omitted (without id, listingId, type etc).
 */
export const getHouseholdApplicationDataFields = lodashPick(editableFieldsFieldgroup);

export function validateHouseholdApplication({ householdApplication, formType, roommates }) {
  typeCheck('formType::NonEmptyString', formType);
  const formValues = getHouseholdApplicationDataFields(householdApplication);
  const formFields = getApplicationFormFields({
    formValues: formValues,
    roommates: roommates,
    formType: formType,
    renterProfile: {
      firstName: 'firstName',
      lastName: 'lastName',
    },
  });

  const errors = createFormValidationFn(formFields)(formValues);
  return Object.keys(errors).length > 0 ? errors : null;
}

export function getApplicationChargeCategory({ application }) {
  typeCheck('application::NonEmptyObject', application);
  typeCheck('application::NonEmptyString', application.category);

  return application.category === HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION
    ? STRIPE_CHARGE_CATEGORY_GUARANTOR_APPLICATION_FEE
    : STRIPE_CHARGE_CATEGORY_APPLICATION_FEE;
}

export function getApplicationFeeUsd({ applicationFee, user }) {
  typeCheck('userRoles::Array', user.roles);
  typeCheck('applicationFee::Number', applicationFee);
  if (user.roles.includes(USER_ROLE_SUNROOM_CLUB)) {
    return lodashRound(applicationFee / 2, 2);
  }
  return applicationFee;
}

export function getApplicationFeeUsdString({ applicationFee, user }) {
  const applicationFeeUsd = getApplicationFeeUsd({ applicationFee, user });
  return applicationFeeUsd.toFixed(2).replace('.00', '');
}

export function isApplicationCompleted({ householdApplication }) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_COMPLETED)
  );
}

export function isApplicationPaid({ householdApplication }) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
      HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_PAID) ||
    !!householdApplication.stripeChargeId
  );
}

export function isApplicationAccepted(householdApplication) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_ACCEPTED)
  );
}

export function isApplicationSigned(householdApplication) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED)
  );
}

export function isApplicationLeaseDraftPending(householdApplication) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING)
  );
}

export function isApplicationLeaseSent(householdApplication) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT)
  );
}

export function isApplicationPastStatus(householdApplication, status) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(status)
  );
}

export function isApplicationDepositPaid(householdApplication) {
  return (
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdApplication.status) >=
    HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID)
  );
}

export function isApplicationSubmissionPaid(applicationSubmission) {
  return !!applicationSubmission.stripeChargeId;
}

export function getHouseholdEarliestApplication(applications) {
  typeCheck('applications::Array', applications);
  for (let i = 0, len = HOUSEHOLD_APPLICATION_STATUS_LIST.length; i < len; i += 1) {
    const status = HOUSEHOLD_APPLICATION_STATUS_LIST[i];
    for (const application of applications) {
      if (application.status === status) {
        return application;
      }
    }
  }

  return null;
}

export function getHouseholdEarliestApplicationStatus(applications) {
  const earlierApplication = getHouseholdEarliestApplication(applications);
  return earlierApplication ? earlierApplication.status : null;
}

export function getApplicationStatusGroup({ listing }) {
  return listing.dataSource === LISTING_DATA_SOURCE_SUNROOM
    ? HOUSEHOLD_APPLICATION_STATUS_GROUP_SR
    : HOUSEHOLD_APPLICATION_STATUS_GROUP_DEFAULT;
}

export function getApplicationStatusOptionItems({ listing }) {
  const applicationStatusGroup = getApplicationStatusGroup({ listing });
  return HOUSEHOLD_APPLICATION_STATUS_OPTION_ITEMS_TO_GROUP_MAPPING[applicationStatusGroup];
}

export function getGuarantorApplicationStatusOptionItems() {
  return HOUSEHOLD_APPLICATION_STATUS_OPTION_ITEMS_TO_GROUP_MAPPING[
    HOUSEHOLD_APPLICATION_STATUS_GROUP_GUARANTOR
  ];
}

export function makeApplicationStatusList({ fromApplicationStatus, toApplicationStatus }) {
  const fromApplicationStatusIndex =
    fromApplicationStatus && HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(fromApplicationStatus);
  const toApplicationStatusIndex =
    toApplicationStatus && HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(toApplicationStatus);

  return HOUSEHOLD_APPLICATION_STATUS_LIST.filter((_, householdApplicationStatusIndex) => {
    return (
      (fromApplicationStatus
        ? fromApplicationStatusIndex <= householdApplicationStatusIndex
        : true) &&
      (toApplicationStatus ? toApplicationStatusIndex >= householdApplicationStatusIndex : true)
    );
  });
}

export function getApplicationMovePeriodFormat(householdApplication) {
  if (householdApplication.movePeriodStartDate && householdApplication.movePeriodEndDate) {
    return `${[
      moment(householdApplication.movePeriodStartDate).format('MMM D'),
      moment(householdApplication.movePeriodEndDate).format('MMM D, YYYY'),
    ].join(' - ')} Move-in Date`;
  }

  return `${moment(householdApplication.moveInDate).format('MM/DD/YYYY')} Move-in Date`;
}

export const omitSensitiveFields = lodashOmit('ssn');

export function getLastApplicationCompletedDate(householdApplications) {
  return householdApplications.reduce((dateMatch, { completedDate }) => {
    if (!completedDate) {
      return dateMatch;
    }
    if (!dateMatch) {
      return completedDate;
    }
    return moment(dateMatch).isBefore(completedDate) ? completedDate : dateMatch;
  }, null);
}

export function getFirstApplicationCompletedDate(householdApplications) {
  return householdApplications.reduce((dateMatch, { completedDate }) => {
    if (!completedDate) {
      return dateMatch;
    }
    if (!dateMatch) {
      return completedDate;
    }
    return moment(dateMatch).isAfter(completedDate) ? completedDate : dateMatch;
  }, null);
}

export function isVerificationOfEmploymentCompleted({ partner, householdApplication }) {
  typeCheck('partner::Maybe Object', partner);
  typeCheck('householdApplication::Object', householdApplication);

  const hasIncomeVerificationCompleted =
    householdApplication.incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED &&
    householdApplication.incomeVerification?.incomeMonthly > 0;

  const hasEmploymentVerificationCompleted =
    householdApplication.employmentVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED &&
    !!householdApplication.employmentVerification?.isReportValid;

  if (
    partner?.acceptBankDataAsVoe &&
    hasIncomeVerificationCompleted &&
    hasEmploymentVerificationCompleted
  ) {
    return true;
  }

  if (householdApplication.verificationOfEmploymentPdfUrl) {
    return true;
  }

  return (householdApplication.verificationOfEmploymentAdditionalDocuments || []).length > 0;
}

export function isVerificationOfRentalRequired({ tenantScreeningCriteria, tenantAlertReport }) {
  typeCheck('tenantScreeningCriteria::Object', tenantScreeningCriteria);
  typeCheck('tenantAlertReport::Maybe Object', tenantAlertReport);

  if (!tenantScreeningCriteria.verifyRentalHistory) {
    return false;
  }

  if (!tenantScreeningCriteria.verifyRentalHistoryOnMinCreditScore) {
    return true;
  }

  if (!tenantAlertReport || tenantAlertReport.creditScore === null) {
    return true;
  }

  return (
    tenantAlertReport.creditScore < tenantScreeningCriteria.verifyRentalHistoryOnMinCreditScore
  );
}

export function isVerificationOfRentalCompleted({
  householdApplication,
  tenantScreeningCriteria,
  tenantAlertReport,
}) {
  typeCheck('householdApplication::Object', householdApplication);
  typeCheck('tenantScreeningCriteria::Object', tenantScreeningCriteria);
  typeCheck('tenantAlertReport::Maybe Object', tenantAlertReport);

  const verificationOfRentalRequired = isVerificationOfRentalRequired({
    tenantScreeningCriteria,
    tenantAlertReport,
  });

  if (!verificationOfRentalRequired) {
    return true;
  }

  if (householdApplication.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_OWN) {
    return !!householdApplication.verificationOfRentalStatus;
  }

  if (householdApplication.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY) {
    return (
      householdApplication.verificationOfRentalStatus ===
      SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
    );
  }

  if (householdApplication.verificationOfRentalPdfUrl) {
    return true;
  }

  return (householdApplication.verificationOfRentalAdditionalDocuments || []).length > 0;
}

export function isEmployed({ householdApplication }) {
  typeCheck('householdApplication::Object', householdApplication);

  return (
    householdApplication.employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED ||
    householdApplication.employmentStatus ===
      HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_RECENTLY_EMPLOYED
  );
}

export function getApplicationLimit(listing) {
  if (!listing) {
    return Number.MAX_VALUE;
  }

  if (isListingTenantScreening(listing)) {
    return Number.MAX_VALUE;
  }

  return 3;
}

export function getApplicantsList({ renters = [], guarantors = [] }) {
  return [...renters, ...guarantors].map((applicant) => applicant.name).join(', ');
}

export function sortHouseholdApplicationsByLeadRenter({
  householdApplications = [],
  leadRenterId,
}) {
  const applications = [];

  for (const application of householdApplications) {
    if (application.userId === leadRenterId) {
      applications.unshift(application);
    } else {
      applications.push(application);
    }
  }

  return applications;
}

export function getApplicationMonthlyIncome({ householdApplication }) {
  typeCheck('householdApplication::Object', householdApplication, {
    errorMessage: 'householdApplication is required',
  });

  if (householdApplication.monthlyIncome !== null) {
    return householdApplication.monthlyIncome;
  }

  if (householdApplication.statedYearlyIncome !== null) {
    return householdApplication.statedYearlyIncome / 12;
  }

  return 0;
}

export function shouldReSendVerificationOfRentalAutoText({ householdApplication, updates }) {
  const hasLandlordEmailChanged =
    updates.currentLandlordEmail &&
    householdApplication.currentLandlordEmail &&
    updates.currentLandlordEmail !== householdApplication.currentLandlordEmail;
  const hasLandlordPhoneChanged =
    updates.currentLandlordPhone &&
    householdApplication.currentLandlordPhone &&
    updates.currentLandlordPhone !== householdApplication.currentLandlordPhone;

  return {
    hasLandlordEmailChanged,
    hasLandlordPhoneChanged,
    reSendVerificationOfRentalAutoText: hasLandlordEmailChanged || hasLandlordPhoneChanged || false,
  };
}

export function shouldReSendVerificationOfEmploymentAutoText({ householdApplication, updates }) {
  const hasSupervisorEmailChanged =
    updates.currentJobEmployerEmail &&
    householdApplication.currentJobEmployerEmail &&
    updates.currentJobEmployerEmail !== householdApplication.currentJobEmployerEmail;

  const hasSupervisorPhoneNumberChanged =
    updates.currentJobSupervisorPhoneNumber &&
    householdApplication.currentJobSupervisorPhoneNumber &&
    updates.currentJobSupervisorPhoneNumber !==
      householdApplication.currentJobSupervisorPhoneNumber;

  return {
    hasSupervisorEmailChanged,
    hasSupervisorPhoneNumberChanged,
    reSendVerificationOfEmploymentAutoText:
      hasSupervisorEmailChanged || hasSupervisorPhoneNumberChanged || false,
  };
}
